import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';

import type { PostalCodeStat, RegionStat } from '../api/types';
import { EmptyState } from '../components/EmptyState';
import { ErrorMessage } from '../components/ErrorMessage';
import { PageLoader } from '../components/Loader';
import { numberFormat } from '../geo/labels';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';

const n = (v: number) => numberFormat.format(v);

// Leads nach Region: Deutschland → Bundesland → Landkreis → PLZ. Alle Zahlen werden
// serverseitig aggregiert; es werden nie einzelne Leads in den Browser geladen.
export function RegionsPage({ embedded = false }: { embedded?: boolean }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const [params] = useSearchParams();
  // Drill-down-Links behalten den Reiter (?ansicht=regionen), wenn die Seite unter „Leads“ eingebettet ist.
  const view = params.get('ansicht');
  const href = (qs: string) => {
    const p = new URLSearchParams(qs);
    if (view) p.set('ansicht', view);
    return `?${p}`;
  };
  const stateId = params.get('state') ?? undefined;
  const countyId = params.get('county') ?? undefined;
  const base = `/p/${project.key}`;

  const states = useQuery({
    queryKey: [...projectKeys.regions(project.id), 'federal-states'],
    queryFn: ({ signal }) => papi.regionStats('federal-states', undefined, signal),
  });
  const counties = useQuery({
    queryKey: [...projectKeys.regions(project.id), 'counties', stateId],
    queryFn: ({ signal }) => papi.regionStats('counties', stateId, signal),
    enabled: !!stateId,
  });
  const postal = useQuery({
    queryKey: [...projectKeys.regions(project.id), 'postal-codes', countyId],
    queryFn: ({ signal }) => papi.postalCodeStats(countyId as string, signal),
    enabled: !!countyId,
  });
  const areas = useQuery({
    queryKey: [...projectKeys.regions(project.id), 'areas'],
    queryFn: ({ signal }) => papi.regionStats('areas', undefined, signal),
    enabled: !stateId && can('area.read'),
  });

  const state = states.data?.items.find((s) => s.id === stateId);
  const county = counties.data?.items.find((c) => c.id === countyId);

  return (
    <div className={embedded ? 'embedded-page' : 'page'}>
      {!embedded && (
        <header className="page-header">
          <h1>Leads nach Region</h1>
        </header>
      )}
      <nav aria-label="Regionspfad" className="breadcrumb">
        <ol>
          <li>{stateId ? <Link to={href('')}>Deutschland</Link> : <span aria-current="page">Deutschland</span>}</li>
          {stateId && (
            <li>
              {countyId ? <Link to={href(`state=${stateId}`)}>{state?.name ?? 'Bundesland'}</Link> : <span aria-current="page">{state?.name ?? '…'}</span>}
            </li>
          )}
          {countyId && (
            <li>
              <span aria-current="page">{county?.name ?? '…'}</span>
            </li>
          )}
        </ol>
      </nav>

      {countyId ? (
        <Section query={postal} empty="In diesem Landkreis gibt es keine Leads.">
          {(items: PostalCodeStat[]) => (
            <StatTable
              caption={`Postleitzahlen in ${county?.name ?? 'diesem Landkreis'}`}
              firstColumn="PLZ / Ort"
              rows={items.map((p) => ({
                key: p.postal_code ?? 'none',
                label: (
                  <>
                    <strong>{p.postal_code ?? 'Ohne PLZ'}</strong> <span className="muted">{p.cities.join(', ')}</span>
                  </>
                ),
                stat: p,
                leadsLink: `${base}/leads?county_id=${countyId}${p.postal_code ? `&postal_code=${p.postal_code}` : ''}`,
              }))}
            />
          )}
        </Section>
      ) : stateId ? (
        <Section query={counties} empty="In diesem Bundesland gibt es keine Leads.">
          {(items: RegionStat[]) => (
            <StatTable
              caption={`Landkreise in ${state?.name ?? 'diesem Bundesland'}`}
              firstColumn="Landkreis"
              rows={items.map((c) => ({
                key: c.id ?? 'none',
                label: c.id ? (
                  <Link to={href(`state=${stateId}&county=${c.id}`)}>
                    {c.name} <span className="muted">({c.type_label})</span>
                  </Link>
                ) : (
                  'Ohne Landkreis'
                ),
                stat: c,
                leadsLink: `${base}/leads?federal_state_id=${stateId}&county_id=${c.id ?? 'none'}`,
              }))}
            />
          )}
        </Section>
      ) : (
        <>
          <Section query={states} empty="In diesem Projekt gibt es noch keine Leads.">
            {(items: RegionStat[]) => (
              <StatTable
                caption="Bundesländer"
                firstColumn="Bundesland"
                rows={items.map((s) => ({
                  key: s.id ?? 'none',
                  label: s.id ? <Link to={href(`state=${s.id}`)}>{s.name}</Link> : 'Ohne Zuordnung (Geo-Klärung)',
                  stat: s,
                  leadsLink: `${base}/leads?federal_state_id=${s.id ?? 'none'}`,
                }))}
              />
            )}
          </Section>
          {can('area.read') && (
            <Section query={areas} empty="Dem Projekt sind noch keine Gebiete zugeordnet.">
              {(items: RegionStat[]) => (
                <StatTable
                  caption="Gebiete"
                  firstColumn="Gebiet"
                  rows={items.map((a) => ({
                    key: a.id ?? 'none',
                    label: a.id ? <Link to={`${base}/gebiete/${a.id}`}>{a.name}</Link> : 'Ohne Gebiet',
                    stat: a,
                    leadsLink: `${base}/leads?area_id=${a.id ?? 'none'}`,
                  }))}
                />
              )}
            </Section>
          )}
        </>
      )}
    </div>
  );
}

interface QueryLike<T> {
  isPending: boolean;
  isError: boolean;
  error: unknown;
  data?: { items: T[] };
  refetch: () => unknown;
}

function Section<T>({ query, empty, children }: { query: QueryLike<T>; empty: string; children: (items: T[]) => ReactNode }) {
  if (query.isPending) return <PageLoader label="Wird berechnet …" />;
  if (query.isError) return <ErrorMessage error={query.error} onRetry={() => void query.refetch()} />;
  const items = query.data?.items ?? [];
  if (items.length === 0) return <EmptyState title="Keine Daten">{empty}</EmptyState>;
  return <>{children(items)}</>;
}

interface Row {
  key: string;
  label: ReactNode;
  stat: { lead_count: number; open_lead_count: number; appointment_count: number };
  leadsLink: string;
}

function StatTable({ caption, firstColumn, rows }: { caption: string; firstColumn: string; rows: Row[] }) {
  const total = rows.reduce((sum, r) => sum + r.stat.lead_count, 0);
  return (
    <div className="table-wrap section">
      <table className="table">
        <caption>
          {caption} <span className="muted">· {n(total)} Leads</span>
        </caption>
        <thead>
          <tr>
            <th scope="col">{firstColumn}</th>
            <th scope="col" className="num">
              Leads
            </th>
            <th scope="col" className="num">
              Offen
            </th>
            <th scope="col" className="num">
              Termine
            </th>
            <th scope="col">
              <span className="visually-hidden">Aktion</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <th scope="row">{r.label}</th>
              <td className="num">{n(r.stat.lead_count)}</td>
              <td className="num">{n(r.stat.open_lead_count)}</td>
              <td className="num">{n(r.stat.appointment_count)}</td>
              <td>
                <Link to={r.leadsLink}>Leads anzeigen</Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
